export type Unit = 'Year' | 'Month' | 'Week' | 'Day' | 'Hour' | 'Minute' | 'Second';

export const UNIT_ORDER: Unit[] = ['Year', 'Month', 'Week', 'Day', 'Hour', 'Minute', 'Second'];

const FIXED_MS: Record<Exclude<Unit, 'Year' | 'Month'>, number> = {
  Week: 604800000,
  Day: 86400000,
  Hour: 3600000,
  Minute: 60000,
  Second: 1000,
};

/// iOS DateUnit bitmask: bits 0..6 = Year, Month, Week, Day, Hour, Minute, Second.
/// Default = Year|Month|Day|Hour|Minute|Second (matches iOS default; Week off).
export const DEFAULT_FORMAT_UNITS_BITMASK = 0b1111011;
const DEFAULT_UNITS: Unit[] = ['Year', 'Month', 'Day', 'Hour', 'Minute', 'Second'];

export function parseFormatUnits(bitmask: number | null | undefined): Unit[] {
  if (typeof bitmask !== 'number' || !Number.isFinite(bitmask)) return [...DEFAULT_UNITS];
  const units = UNIT_ORDER.filter((_, bit) => (bitmask & (1 << bit)) !== 0);
  return units.length > 0 ? units : [...DEFAULT_UNITS];
}

export function unitsToBitmask(units: Unit[]): number {
  let m = 0;
  for (const u of units) {
    const bit = UNIT_ORDER.indexOf(u);
    if (bit >= 0) m |= 1 << bit;
  }
  return m;
}

/// Calendar-aware cascading decomposition of the diff between `from` and `to`
/// across the enabled units, in iOS order. Mirrors
/// functions/views/countdown.hbs so PWA-rendered countdowns match the
/// server-rendered /to page exactly (including across leap years and DST).
///
/// Year/Month consume whole calendar units; Week/Day/Hour/Minute/Second use
/// fixed millisecond chunks of the remainder. Caller must pass `from <= to`
/// (negate up front for past dates).
export function decompose(
  from: Date,
  to: Date,
  units: Unit[],
  countOnlyWeekdays = false,
): Record<Unit, number> {
  const enabled = UNIT_ORDER.filter((u) => units.includes(u));
  const result: Partial<Record<Unit, number>> = {};

  // A format with no Hour/Minute/Second is read over whole calendar days,
  // from the start of the first day to the end of the last, as
  // compactTimeLeftFrom here and DateFormattingUtils.adjustedDate on iOS do,
  // so a timed event looked at past its own time of day is not a unit short.
  // Its Week/Day figures are counted in calendar days below, not fixed
  // milliseconds, so a DST hour inside the span cannot add or drop a day.
  const calendarOnly = !(
    enabled.includes('Hour') || enabled.includes('Minute') || enabled.includes('Second')
  );
  let cursor = calendarOnly ? startOfDay(from) : new Date(from.getTime());
  const end = calendarOnly ? endOfDay(to) : to;

  // Count-only-weekdays: the viewer's weekend days are skipped, mirroring
  // DateFormattingUtils.adjustedDate in the iOS app. Whole enabled
  // Year/Month/Week units are stepped off first (their days are not skipped),
  // then the start moves forward by the weekend days left before `to`, so
  // the Day figure counts weekdays only.
  if (countOnlyWeekdays) {
    let wk = startOfDay(from);
    const toDay = startOfDay(to);
    for (const unit of enabled) {
      if (unit !== 'Year' && unit !== 'Month' && unit !== 'Week') continue;
      while (addUnit(wk, unit) < toDay) wk = addUnit(wk, unit);
    }
    cursor = addDays(cursor, weekendDaysBetween(wk, toDay));
  }

  for (const unit of enabled) {
    if (unit === 'Year' || unit === 'Month') {
      const monthSpan = unit === 'Year' ? 12 : 1;
      let n =
        unit === 'Year'
          ? end.getFullYear() - cursor.getFullYear()
          : (end.getFullYear() - cursor.getFullYear()) * 12 + (end.getMonth() - cursor.getMonth());
      let probe = addMonthsClamped(cursor, n * monthSpan);
      if (probe > end) {
        n -= 1;
        probe = addMonthsClamped(cursor, n * monthSpan);
      }
      result[unit] = n;
      cursor = probe;
    } else if (calendarOnly) {
      // cursor sits at a local midnight here; whole days to end's midnight.
      const days = Math.round((startOfDay(end).getTime() - cursor.getTime()) / FIXED_MS.Day);
      const n = unit === 'Week' ? Math.floor(days / 7) : days;
      result[unit] = n;
      cursor = addDays(cursor, unit === 'Week' ? n * 7 : n);
    } else {
      const ms = FIXED_MS[unit];
      const n = Math.floor((end.getTime() - cursor.getTime()) / ms);
      result[unit] = n;
      cursor = new Date(cursor.getTime() + n * ms);
    }
  }
  return result as Record<Unit, number>;
}

export function turnsYearsFor(
  kind: 'birthday' | 'anniversary' | null,
  originYear: number | null,
  dueDate: Date,
): number | null {
  if (!kind || !originYear) return null;
  return dueDate.getFullYear() - originYear;
}

/// Single-unit "compact" countdown for the list cell. Mirrors
/// DateFormattingUtils.compactTimeLeftFrom in iOS:
/// the user picks one of Year/Month/Week/Day; if that unit rounds to 0 we
/// cascade down (Year → Month → Week → Day → Hour → Minute → Second) until
/// we find a non-zero count. Past events return a negative `number`.
///
/// Day/Hour/Minute/Second snap both ends to the start of their unit (mirroring
/// iOS's Calendar.dateComponents floor) so a date 5 hours away at 23:30 still
/// reads "1 day" once it crosses midnight in the user's calendar — same trick
/// CountdownTableViewCell applies.
export function compactTimeLeftFrom(
  fromDate: Date,
  toDate: Date,
  compactUnit: 'Year' | 'Month' | 'Week' | 'Day',
  countOnlyWeekdays = false,
): { number: number; unit: Unit } {
  const fromMs = fromDate.getTime();
  const toMs = toDate.getTime();
  const isPast = toMs <= fromMs;
  const sameDay =
    startOfDay(fromDate).getTime() === startOfDay(toDate).getTime();
  if (isPast && sameDay) return { number: 0, unit: 'Second' };

  const sign = isPast ? -1 : 1;
  const first = isPast ? toDate : fromDate;
  const second = isPast ? fromDate : toDate;

  let cascade = false;

  // Years, months and weeks over whole calendar days, the way the day count
  // below reads them and iOS does: from the start of the first day to the end
  // of the last. Over the two instants, a timed event looked at a few hours
  // past its own time of day is short of the unit by those hours and reads one
  // less than its day count says — "28 days" and "3 weeks" for the same date.
  const firstDay = startOfDay(first);
  const lastDay = endOfDay(second);

  if (compactUnit === 'Year') {
    const years = calendarUnits(firstDay, lastDay, 'Year');
    if (years > 0) return { number: years * sign, unit: 'Year' };
    cascade = true;
  }
  if (compactUnit === 'Month' || cascade) {
    const months = calendarUnits(firstDay, lastDay, 'Month');
    if (months > 0) return { number: months * sign, unit: 'Month' };
    cascade = true;
  }
  if (compactUnit === 'Week' || cascade) {
    // round() over start-of-day marks absorbs DST's ±1h, as the day count does.
    const calendarDays = Math.round((startOfDay(second).getTime() - firstDay.getTime()) / FIXED_MS.Day);
    const weeks = Math.floor(calendarDays / 7);
    if (weeks > 0) return { number: weeks * sign, unit: 'Week' };
  }

  // Day count = the number of calendar-day boundaries between start-of-day(first)
  // and start-of-day(second), mirroring iOS's Calendar.dateComponents([.day])
  // floor — so it ticks down exactly at midnight, not 24h-from-now. round() (not
  // a fixed-ms floor) absorbs DST's ±1h drift across the span.
  const daysRaw = Math.floor((second.getTime() - first.getTime()) / FIXED_MS.Day);
  if (daysRaw > 0) {
    // Weekdays only: the start moves past the weekend days in the span, as
    // DateFormattingUtils.adjustedDate does on iOS.
    let firstDay = startOfDay(first);
    if (countOnlyWeekdays) {
      firstDay = addDays(firstDay, weekendDaysBetween(firstDay, startOfDay(second)));
    }
    const days = Math.round((startOfDay(second).getTime() - firstDay.getTime()) / FIXED_MS.Day);
    return { number: days * sign, unit: 'Day' };
  }

  const hoursRaw = Math.floor((second.getTime() - first.getTime()) / FIXED_MS.Hour);
  if (hoursRaw > 0) {
    const hours = Math.round(
      (startOfHour(second).getTime() - startOfHour(first).getTime()) / FIXED_MS.Hour,
    );
    return { number: hours * sign, unit: 'Hour' };
  }

  const minutesRaw = Math.floor((second.getTime() - first.getTime()) / FIXED_MS.Minute);
  if (minutesRaw > 0) {
    const minutes = Math.round(
      (startOfMinute(second).getTime() - startOfMinute(first).getTime()) / FIXED_MS.Minute,
    );
    return { number: minutes * sign, unit: 'Minute' };
  }

  const seconds = Math.floor((second.getTime() - first.getTime()) / FIXED_MS.Second);
  if (seconds > 0) return { number: seconds * sign, unit: 'Second' };
  return { number: 0, unit: 'Second' };
}

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}
function endOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
}
function startOfHour(d: Date): Date {
  const c = new Date(d);
  c.setMinutes(0, 0, 0);
  return c;
}
function startOfMinute(d: Date): Date {
  const c = new Date(d);
  c.setSeconds(0, 0);
  return c;
}
function addDays(d: Date, days: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c;
}
function addUnit(d: Date, unit: 'Year' | 'Month' | 'Week'): Date {
  return unit === 'Week' ? addDays(d, 7) : addMonthsClamped(d, unit === 'Year' ? 12 : 1);
}

/// The viewer's weekend as ISO weekdays (1 = Monday … 7 = Sunday), from the
/// browser's region: Friday–Saturday in Saudi Arabia, Saturday–Sunday in most
/// places. iOS reads the same thing from Calendar.isDateInWeekend, so the
/// reader counts with their own weekend, as the app does.
const WEEKEND: number[] = (() => {
  try {
    const loc = new Intl.Locale(navigator.language) as Intl.Locale & {
      getWeekInfo?: () => { weekend?: number[] };
      weekInfo?: { weekend?: number[] };
    };
    const info = typeof loc.getWeekInfo === 'function' ? loc.getWeekInfo() : loc.weekInfo;
    if (info && Array.isArray(info.weekend) && info.weekend.length) return info.weekend;
  } catch {
    // no Intl.Locale week info: Saturday–Sunday
  }
  return [6, 7];
})();

/// Weekend days in [from, to), both at local midnight. Mirrors
/// Date.weekdays(from:) in the iOS app: whole weeks at once, the remainder
/// walked day by day.
function weekendDaysBetween(from: Date, to: Date): number {
  const total = Math.round((to.getTime() - from.getTime()) / FIXED_MS.Day);
  if (total <= 0) return 0;
  let n = Math.floor(total / 7) * WEEKEND.length;
  let d = from;
  for (let i = 0; i < total % 7; i++) {
    if (WEEKEND.includes(d.getDay() || 7)) n += 1;
    d = addDays(d, 1);
  }
  return n;
}
/// Whole-calendar-unit count from a → b for Year/Month, using the same
/// "advance by N and back off if we overshot" pattern as iOS's
/// Date.years(from:)/months(from:) extensions.
function calendarUnits(a: Date, b: Date, unit: 'Year' | 'Month'): number {
  let n =
    unit === 'Year'
      ? b.getFullYear() - a.getFullYear()
      : (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (addMonthsClamped(a, n * (unit === 'Year' ? 12 : 1)) > b) n -= 1;
  return n;
}

/// Add whole calendar months with NSCalendar's clamping semantics: Aug 31 + 1
/// month is Sep 30, not the setMonth overflow into Oct 1 (and Feb 29 + 12
/// months is Feb 28). Always builds a fresh Date from `d` — re-mutating an
/// overflowed probe keeps the corrupted day-of-month (Aug 31 → "Sep 31" →
/// Oct 1, then un-adding lands on Aug 1 and inflates the countdown by 30 days).
function addMonthsClamped(d: Date, months: number): Date {
  const r = new Date(d.getTime());
  const day = r.getDate();
  r.setDate(1);
  r.setMonth(r.getMonth() + months);
  const last = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(day, last));
  return r;
}

/// Hide units whose current value is zero (e.g. "3 months out" shouldn't read
/// as "0Y 3M 0W 5D 0H 0M 0S"). Exception: the smallest enabled unit is always
/// kept visible regardless of value — when it's the rapidly ticking one (e.g.
/// Seconds), hiding it at 0 would make it flicker on/off every minute. Same
/// rule applies if the user disabled smaller units and the smallest is, say,
/// Minutes: still pin it. Mirrors countdown.hbs:update.
export function visibleUnitsFor(
  enabled: Unit[],
  parts: Record<Unit, number>,
): Unit[] {
  if (enabled.length === 0) return [];
  const smallest = enabled[enabled.length - 1];
  return enabled.filter((u) => u === smallest || parts[u] !== 0);
}
